/**
 * @file AppLocaleSync.tsx
 * @description Drives the i18next runtime's active locale from the signed-in
 * user's `languagePreference`. Renders nothing.
 *
 * ## Why this exists
 *
 * SE-6507 built `setAppLocale` and the whole locale-change path, and SE-6505
 * built the resolution rules underneath it — but nothing in the app ever CALLED
 * `setAppLocale`. The runtime was mounted and correct and permanently pinned to
 * `DEFAULT_UI_LOCALE`, because the one wire from the user's stored preference
 * to the runtime was never run.
 *
 * That gap is invisible from either end. The i18n unit tests pass: they drive
 * `setAppLocale` directly. The profile write works: the AI-summary picker
 * persists `languagePreference` through `updateMyProfile`. Only the join
 * between them was missing, so a user could pick German, have it saved, and see
 * an English app with no error anywhere.
 *
 * SE-7966 is the ticket that needs this join to exist — every formatter it
 * introduces reads the active UI locale, and without this component that locale
 * is `en_US` for everyone forever, which would make the entire change a no-op
 * in production while passing every test.
 *
 * ## Placement
 *
 * Needs `CurrentUserProvider` above it (for the profile) and `I18nProvider`
 * above it (so the instance it mutates is the one the tree renders through).
 * Mounted in `app/_layout.tsx` directly inside `I18nProvider`, which is inside
 * `CurrentUserProvider` — moving it above either breaks it at runtime rather
 * than at compile time. Same constraint, and the same headless shape, as
 * `EvaluationOutboxSync`.
 * @module i18n
 */
import { useEffect } from "react";

import { setAppLocale } from "./instance";

import { useCurrentUser } from "@/features/auth/providers/CurrentUser";

/**
 * Keeps the i18next runtime's language in step with the account's
 * `languagePreference`.
 *
 * Signed out, `languagePreference` is `undefined` and `setAppLocale` resolves
 * that to `DEFAULT_UI_LOCALE` — so signing out returns the app to English
 * rather than leaving the previous user's language on the sign-in screen.
 *
 * The effect depends on the preference STRING, not on the user object, so an
 * Apollo refetch that returns an identically-configured user does not re-run a
 * language change.
 * @returns null — this component renders nothing
 */
export function AppLocaleSync(): null {
  const currentUser = useCurrentUser();
  const preference = currentUser?.languagePreference;

  useEffect(() => {
    void setAppLocale(preference);
  }, [preference]);

  return null;
}
